import {
  LayoutDashboard,
  Server,
  Globe,
  Receipt,
  LifeBuoy,
  BookOpen,
} from 'lucide-react';
import type { TopNavItem, SubNavItem } from '@sighthost/ui';

/**
 * Client app primary navigation — shown in `UniversalTopNav`.
 *
 * Top-level items mirror the locked product scope: Dashboard, Services,
 * Domains, Billing, Support, Knowledgebase. Each section that has more than
 * one page also exposes a sub-nav row (clientSubNavByPrefix below) so users
 * land on a section header and can hop between its pages without a sidebar.
 */
export const clientPrimaryNav: TopNavItem[] = [
  { label: 'Dashboard',     to: '/app',               end: true, icon: LayoutDashboard },
  { label: 'Services',      to: '/app/services',      icon: Server },
  { label: 'Domains',       to: '/app/domains',       icon: Globe },
  { label: 'Billing',       to: '/app/billing',       icon: Receipt },
  { label: 'Support',       to: '/app/support',       icon: LifeBuoy, end: true },
  { label: 'Knowledgebase', to: '/app/knowledgebase', icon: BookOpen },
];

/** Section sub-nav configs — rendered when the active route is inside the section. */

export const clientServicesSubNav: SubNavItem[] = [
  { label: 'My services', to: '/app/services',     end: true },
  { label: 'Order new',   to: '/app/services/new'  },
];

export const clientDomainsSubNav: SubNavItem[] = [
  { label: 'My domains',   to: '/app/domains',          end: true },
  { label: 'WHOIS lookup', to: '/app/domains/whois'     },
  { label: 'Register',     to: '/app/domains/register'  },
  { label: 'Transfer',     to: '/app/domains/transfer'  },
];

export const clientBillingSubNav: SubNavItem[] = [
  { label: 'Invoices',        to: '/app/billing',                 end: true },
  { label: 'Credit notes',    to: '/app/billing/credit-notes'     },
  { label: 'Statements',      to: '/app/billing/statements'       },
  { label: 'Payment methods', to: '/app/billing/payment-methods'  },
];

export const clientSupportSubNav: SubNavItem[] = [
  { label: 'Tickets',  to: '/app/support',          end: true },
  { label: 'New ticket', to: '/app/support/tickets/new' },
];

export const clientKnowledgebaseSubNav: SubNavItem[] = [
  { label: 'All articles', to: '/app/knowledgebase',           end: true },
  { label: 'Hosting',      to: '/app/knowledgebase/hosting'    },
  { label: 'Domains',      to: '/app/knowledgebase/domains'    },
  { label: 'Billing',      to: '/app/knowledgebase/billing'    },
  { label: 'Account',      to: '/app/knowledgebase/account'    },
];

/**
 * Map of route prefix → sub-nav config. Used by ClientLayoutWrapper to render
 * the right sub-nav row for the active section. Order matters — longer prefixes
 * must come before shorter parents (e.g. /app/support/tickets before /app/support
 * if they ever diverge).
 */
export const clientSubNavByPrefix: Array<{ prefix: string; items: SubNavItem[] }> = [
  { prefix: '/app/services',      items: clientServicesSubNav      },
  { prefix: '/app/domains',       items: clientDomainsSubNav       },
  { prefix: '/app/billing',       items: clientBillingSubNav       },
  { prefix: '/app/support',       items: clientSupportSubNav       },
  { prefix: '/app/knowledgebase', items: clientKnowledgebaseSubNav },
];
